import { useEffect, useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    ClipboardList,
    GitBranch,
    HelpCircle,
    Lightbulb,
    Star,
} from "lucide-react";
import { clientApi } from "@/lib/clientAuth";

// Decision/level/category label maps (kept short to avoid duplication)
const OP_DECISION = {
    en_etude: "En étude",
    go: "GO",
    go_conditions: "GO sous conditions",
    no_go: "NO GO",
    suspendu: "Suspendu",
};
const COMITE_DECISION = {
    go: "GO",
    go_conditions: "GO sous conditions",
    no_go: "NO GO",
};
const ALERT_COLOR = {
    green: "#7fb069",
    orange: "#e6a23c",
    red: "#e89090",
};
const VALUE_CATEGORY = {
    partner: "Identification d'un partenaire",
    risk: "Risque évité",
    financing: "Piste de financement",
    structuration: "Proposition de structuration",
    legal: "Optimisation juridique",
    relation: "Mise en relation qualifiée",
    other: "Apport",
};

// ─────────────────────────────────────────────────────────────
// MAIN VIEW — read-only client view of enabled modules
// ─────────────────────────────────────────────────────────────
export default function ClientDossierModules() {
    const [data, setData] = useState(null);

    useEffect(() => {
        clientApi
            .get("/dossier/modules")
            .then((r) => setData(r.data))
            .catch(() => setData({ enabled: {} }));
    }, []);

    if (!data) return null;
    const enabled = data.enabled || {};
    const anyEnabled = Object.values(enabled).some(Boolean);
    if (!anyEnabled) return null;

    return (
        <section
            data-testid="client-modules"
            className="mb-16 space-y-12"
        >
            {enabled.pipeline && data.pipeline && (
                <PipelineView pipeline={data.pipeline} />
            )}
            {enabled.committee && (data.committee || []).length > 0 && (
                <CommitteeView items={data.committee} />
            )}
            {enabled.opportunities && (data.opportunities || []).length > 0 && (
                <OpportunitiesView items={data.opportunities} />
            )}
            {enabled.alerts && (data.alerts || []).length > 0 && (
                <AlertsView items={data.alerts} />
            )}
            {enabled.questions && (data.questions || []).length > 0 && (
                <QuestionsView items={data.questions} />
            )}
            {enabled.value_added && (data.value_added || []).length > 0 && (
                <ValueAddedView items={data.value_added} />
            )}
            {enabled.documents && (data.documents || []).length > 0 && (
                <DocumentsView items={data.documents} />
            )}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// Card shell — premium private banking
// ─────────────────────────────────────────────────────────────
function ModuleCard({ icon: Icon, kicker, title, subtitle, children, testid }) {
    return (
        <section
            data-testid={testid}
            className="bg-[#070d18] border border-[#d4af37]/15 p-10 lg:p-12"
        >
            <div className="overline text-[#d4af37]/70 mb-3">
                <Icon
                    strokeWidth={1}
                    size={13}
                    className="inline-block mr-2 -mt-0.5 text-[#d4af37]"
                />
                {kicker}
            </div>
            <h2
                className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight mb-2"
                style={{ fontWeight: 400 }}
            >
                {title}
            </h2>
            {subtitle && (
                <p className="text-[12.5px] text-[#f4ecd8]/45 italic font-light mb-8 leading-relaxed max-w-3xl">
                    {subtitle}
                </p>
            )}
            {children}
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// PIPELINE
// ─────────────────────────────────────────────────────────────
function PipelineView({ pipeline }) {
    const currentIdx = pipeline.stages.findIndex(
        (s) => s.key === pipeline.current_stage,
    );
    return (
        <ModuleCard
            icon={GitBranch}
            kicker="Pipeline stratégique"
            title="Avancement de votre opération"
            subtitle="Étapes successives d'élaboration et de finalisation."
            testid="client-module-pipeline"
        >
            <ol
                data-testid="client-pipeline-stages"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
            >
                {pipeline.stages.map((s, i) => {
                    const passed = currentIdx >= 0 && i < currentIdx;
                    const active = i === currentIdx;
                    return (
                        <li
                            key={s.key}
                            data-testid={`client-pipeline-${s.key}`}
                            className={`relative px-4 py-4 border ${
                                active
                                    ? "border-[#d4af37] bg-[#d4af37]/10"
                                    : passed
                                      ? "border-[#d4af37]/40 bg-[#d4af37]/[0.04]"
                                      : "border-white/[0.08] bg-white/[0.02]"
                            }`}
                        >
                            <div
                                className={`text-[10px] tracking-[0.28em] uppercase mb-2 ${
                                    active
                                        ? "text-[#d4af37]"
                                        : passed
                                          ? "text-[#d4af37]/60"
                                          : "text-[#f4ecd8]/35"
                                }`}
                            >
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <div
                                className={`font-serif text-[14.5px] leading-tight ${
                                    active
                                        ? "text-[#f4ecd8]"
                                        : passed
                                          ? "text-[#f4ecd8]/85"
                                          : "text-[#f4ecd8]/55"
                                }`}
                            >
                                {s.label}
                            </div>
                            {active && (
                                <div className="mt-2 text-[10px] tracking-[0.26em] uppercase text-[#d4af37]">
                                    Étape en cours
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </ModuleCard>
    );
}

// ─────────────────────────────────────────────────────────────
// COMMITTEE (SWOT notes)
// ─────────────────────────────────────────────────────────────
function CommitteeView({ items }) {
    return (
        <ModuleCard
            icon={Star}
            kicker="Comité d'investissement XV"
            title="Notes stratégiques du comité"
            subtitle="Analyses formelles produites par le comité d'investissement du cabinet."
            testid="client-module-committee"
        >
            <ul className="space-y-10">
                {items.map((n) => (
                    <li
                        key={n.id}
                        data-testid={`client-committee-${n.id}`}
                        className="border-l border-[#d4af37]/35 pl-6"
                    >
                        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
                            <h3
                                className="font-serif text-[18px] text-[#f4ecd8]"
                                style={{ fontWeight: 400 }}
                            >
                                {n.title}
                            </h3>
                            <div className="text-[10px] tracking-[0.26em] uppercase text-[#f4ecd8]/40">
                                {n.date
                                    ? new Date(n.date).toLocaleDateString("fr-FR", {
                                          day: "2-digit",
                                          month: "long",
                                          year: "numeric",
                                      })
                                    : ""}
                            </div>
                        </div>
                        {n.avis && (
                            <p className="font-serif text-[14.5px] text-[#f4ecd8]/85 leading-[1.95] mb-6 italic">
                                {n.avis}
                            </p>
                        )}
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-6">
                            <SwotBlock label="Forces" items={n.forces} accent />
                            <SwotBlock label="Faiblesses" items={n.faiblesses} />
                            <SwotBlock label="Opportunités" items={n.opportunites} accent />
                            <SwotBlock label="Risques" items={n.risques} />
                        </div>
                        {n.recommandation && (
                            <div className="mb-4">
                                <div className="overline text-[#d4af37]/70 mb-2">
                                    Recommandation
                                </div>
                                <p className="font-serif text-[14.5px] text-[#f4ecd8]/85 leading-[1.85]">
                                    {n.recommandation}
                                </p>
                            </div>
                        )}
                        {n.decision && (
                            <div className="inline-flex items-center gap-2 mt-2 px-4 py-2 border border-[#d4af37]/50 text-[#d4af37] text-[11px] tracking-[0.28em] uppercase">
                                Décision · {COMITE_DECISION[n.decision] || n.decision}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </ModuleCard>
    );
}

function SwotBlock({ label, items, accent }) {
    if (!items || items.length === 0) return null;
    return (
        <div>
            <div
                className={`overline mb-2 ${
                    accent ? "text-[#d4af37]/85" : "text-[#f4ecd8]/55"
                }`}
            >
                {label}
            </div>
            <ul className="space-y-1.5">
                {items.map((s, i) => (
                    <li
                        key={i}
                        className="text-[13px] font-serif text-[#f4ecd8]/80 leading-snug"
                    >
                        — {s}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// OPPORTUNITIES
// ─────────────────────────────────────────────────────────────
function OpportunitiesView({ items }) {
    return (
        <ModuleCard
            icon={Lightbulb}
            kicker="Opportunités en étude"
            title="Projets analysés par le cabinet"
            subtitle="Pistes étudiées en parallèle de votre dossier principal."
            testid="client-module-opportunities"
        >
            <ul className="space-y-6">
                {items.map((o) => (
                    <li
                        key={o.id}
                        data-testid={`client-opportunity-${o.id}`}
                        className="border-l border-[#d4af37]/30 pl-5"
                    >
                        <div className="flex items-baseline justify-between flex-wrap gap-3">
                            <h3
                                className="font-serif text-[17px] text-[#f4ecd8]"
                                style={{ fontWeight: 400 }}
                            >
                                {o.name}
                            </h3>
                            <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/85">
                                {OP_DECISION[o.decision] || o.decision}
                            </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/40">
                            {o.status && <span>Statut · {o.status}</span>}
                            {o.priority && <span>Priorité · {o.priority}</span>}
                            {o.opened_at && (
                                <span>
                                    Ouvert ·{" "}
                                    {new Date(o.opened_at).toLocaleDateString("fr-FR", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </span>
                            )}
                            {o.potential_value && <span>Valeur · {o.potential_value}</span>}
                        </div>
                        {o.why_studied && (
                            <p className="mt-3 text-[13.5px] text-[#f4ecd8]/75 leading-[1.85] font-light">
                                {o.why_studied}
                            </p>
                        )}
                        {o.risks && (
                            <div className="mt-3">
                                <div className="overline text-[#f4ecd8]/45 mb-1">Risques</div>
                                <p className="text-[12.5px] text-[#f4ecd8]/65 leading-[1.7] font-light">
                                    {o.risks}
                                </p>
                            </div>
                        )}
                        {o.actions_done && (
                            <div className="mt-3">
                                <div className="overline text-[#d4af37]/70 mb-1">Actions XV</div>
                                <p className="text-[12.5px] text-[#f4ecd8]/65 leading-[1.7] font-light">
                                    {o.actions_done}
                                </p>
                            </div>
                        )}
                        {o.next_steps && (
                            <div className="mt-3">
                                <div className="overline text-[#d4af37]/70 mb-1">Prochaines étapes</div>
                                <p className="text-[12.5px] text-[#f4ecd8]/65 leading-[1.7] font-light">
                                    {o.next_steps}
                                </p>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </ModuleCard>
    );
}

// ─────────────────────────────────────────────────────────────
// ALERTS
// ─────────────────────────────────────────────────────────────
function AlertsView({ items }) {
    return (
        <ModuleCard
            icon={AlertTriangle}
            kicker="Alertes stratégiques"
            title="Points d'attention identifiés"
            subtitle="Signaux suivis par le cabinet pour préserver vos intérêts."
            testid="client-module-alerts"
        >
            <ul className="space-y-4">
                {items.map((a) => (
                    <li
                        key={a.id}
                        data-testid={`client-alert-${a.id}`}
                        className="flex items-start gap-4 border border-white/[0.06] bg-white/[0.02] px-5 py-4"
                    >
                        <span
                            className="mt-2 w-3 h-3 rounded-full shrink-0"
                            style={{ background: ALERT_COLOR[a.level] || "#888" }}
                        />
                        <div className="flex-1">
                            <div className="font-serif text-[15px] text-[#f4ecd8] leading-snug">
                                {a.description}
                            </div>
                            {a.consequences && (
                                <p className="mt-2 text-[12.5px] text-[#f4ecd8]/55 leading-[1.75]">
                                    <span className="overline text-[#f4ecd8]/40 mr-2">
                                        Conséquence
                                    </span>
                                    {a.consequences}
                                </p>
                            )}
                            {a.actions_xv && (
                                <p className="mt-1.5 text-[12.5px] text-[#d4af37]/80 leading-[1.75]">
                                    <span className="overline text-[#d4af37]/70 mr-2">
                                        Action XV
                                    </span>
                                    {a.actions_xv}
                                </p>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </ModuleCard>
    );
}

// ─────────────────────────────────────────────────────────────
// QUESTIONS
// ─────────────────────────────────────────────────────────────
function QuestionsView({ items }) {
    return (
        <ModuleCard
            icon={HelpCircle}
            kicker="Questions stratégiques à l'étude"
            title="Arbitrages en cours"
            subtitle="Réflexions actuellement menées par le cabinet sur votre dossier."
            testid="client-module-questions"
        >
            <ul className="space-y-7">
                {items.map((q) => (
                    <li
                        key={q.id}
                        data-testid={`client-question-${q.id}`}
                        className="border-l border-[#d4af37]/30 pl-5"
                    >
                        <h3
                            className="font-serif text-[17px] text-[#f4ecd8] leading-snug"
                            style={{ fontWeight: 400 }}
                        >
                            {q.question}
                        </h3>
                        {(q.options || []).length > 0 && (
                            <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                                {q.options.map((opt, i) => (
                                    <li
                                        key={i}
                                        className="text-[13px] font-serif text-[#f4ecd8]/75"
                                    >
                                        ○ {opt}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {q.xv_analysis && (
                            <div className="mt-4">
                                <div className="overline text-[#d4af37]/70 mb-2">
                                    Analyse XV
                                </div>
                                <p className="text-[13.5px] text-[#f4ecd8]/80 leading-[1.85] font-light italic">
                                    {q.xv_analysis}
                                </p>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </ModuleCard>
    );
}

// ─────────────────────────────────────────────────────────────
// VALUE ADDED
// ─────────────────────────────────────────────────────────────
function ValueAddedView({ items }) {
    return (
        <ModuleCard
            icon={CheckCircle2}
            kicker="Valeur ajoutée créée par XV"
            title="Apports concrets du cabinet"
            subtitle="Interventions structurantes réalisées au bénéfice de votre dossier."
            testid="client-module-value-added"
        >
            <ul className="space-y-5">
                {items.map((v) => (
                    <li
                        key={v.id}
                        data-testid={`client-value-${v.id}`}
                        className="border-l border-[#d4af37]/30 pl-5"
                    >
                        <div className="flex items-baseline justify-between flex-wrap gap-3">
                            <h3
                                className="font-serif text-[16px] text-[#f4ecd8]"
                                style={{ fontWeight: 400 }}
                            >
                                {v.title}
                            </h3>
                            <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/75">
                                {VALUE_CATEGORY[v.category] || v.category}
                            </span>
                        </div>
                        {v.date && (
                            <div className="text-[10px] tracking-[0.24em] uppercase text-[#f4ecd8]/35 mt-1">
                                {new Date(v.date).toLocaleDateString("fr-FR", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </div>
                        )}
                        {v.description && (
                            <p className="mt-2 text-[13.5px] text-[#f4ecd8]/75 leading-[1.85] font-light">
                                {v.description}
                            </p>
                        )}
                    </li>
                ))}
            </ul>
        </ModuleCard>
    );
}

// ─────────────────────────────────────────────────────────────
// DOCUMENTS CHECKLIST
// ─────────────────────────────────────────────────────────────
function DocumentsView({ items }) {
    const received = items.filter((d) => d.status === "received");
    const expected = items.filter((d) => d.status === "expected");
    return (
        <ModuleCard
            icon={ClipboardList}
            kicker="Documents attendus"
            title="État de votre dossier documentaire"
            subtitle="Suivi des éléments transmis et de ceux encore attendus pour l'avancement de l'opération."
            testid="client-module-documents"
        >
            <div className="grid sm:grid-cols-2 gap-10">
                <DocColumn
                    title="Documents reçus"
                    items={received}
                    color="#7fb069"
                    emptyLabel="En attente de transmission."
                    testid="client-docs-received"
                />
                <DocColumn
                    title="Documents attendus"
                    items={expected}
                    color="#e6a23c"
                    emptyLabel="Plus de documents en attente."
                    testid="client-docs-expected"
                />
            </div>
        </ModuleCard>
    );
}

function DocColumn({ title, items, color, emptyLabel, testid }) {
    return (
        <div data-testid={testid}>
            <div className="overline text-[#d4af37]/70 mb-4">{title}</div>
            <ul className="space-y-2">
                {items.length === 0 && (
                    <li className="text-[12px] text-[#f4ecd8]/35 italic">
                        {emptyLabel}
                    </li>
                )}
                {items.map((d) => (
                    <li
                        key={d.id}
                        className="flex items-center gap-3 py-1.5"
                    >
                        <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ background: color }}
                        />
                        <span className="font-serif text-[14px] text-[#f4ecd8]/90 flex-1">
                            {d.name}
                        </span>
                        {d.category && (
                            <span className="text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/35">
                                {d.category}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
